import { forwardRef, useCallback, useEffect, useId, useRef, useState } from "react";
import type { CSSProperties, KeyboardEvent as ReactKeyboardEvent } from "react";
import { createPortal } from "react-dom";
import { ListFilter } from "lucide-react";
import { Button } from "./Button";

// 表頭漏斗的**區間**變體。業主要的四個漏斗裡有三個是多選清單(客戶 / 狀態 /
// 負責同仁,走 HeaderFilter),只有日期那一個不是。
//
// ── 日期為什麼不能用勾選清單 ─────────────────────────────────────────
//
// 勾選清單要先有一份「可選的值」。日期是連續的:表上出現過的每一天都是一個
// 選項,一年就是三百多個勾選框,而使用者真正想表達的是「九月那一段」——
// 他得一天一天勾三十次才說得出來,而且漏掉一天就少一張單。
// 區間兩格輸入是同一件事的正確形狀:起、迄各一個,中間全包。
//
// 這一支與 HeaderFilter 共用的三條規矩(它們不是裝飾,是這個浮層能不能用的前提):
//
// 1. **portal 到 body + position: fixed。** 表格外層是 overflow-x-auto,
//    CSS 規範說 overflow-x 非 visible 時 overflow-y 的 visible 會被算成 auto,
//    絕對定位的浮層會被那個容器裁掉。
// 2. **關閉後焦點回到漏斗。** 否則鍵盤使用者一收起浮層就被丟回頁首。
// 3. **設了幾端要用數字說。** 不可以只靠漏斗圖示變色暗示「現在有在篩」,
//    色弱與單色列印都讀不出來。

/** 浮層寬度。兩格日期並排剛好,固定寬度比自適應穩。 */
const PANEL_WIDTH = 248;
/** 浮層的概估高度,用來判斷要往下開還是往上翻。 */
const PANEL_HEIGHT = 208;

export interface DateRange {
  /** 起日,YYYY-MM-DD。空字串代表「沒有這一端」,不是某個預設日期。 */
  from: string;
  /** 迄日,YYYY-MM-DD。空字串同上。 */
  to: string;
}

export interface DateRangeFilterProps {
  /**
   * 這一欄的名稱,例如「報價日」。
   * 無障礙名稱由它組出來:「篩選報價日」「報價日起日」「報價日迄日」。
   */
  label: string;
  /** 目前的區間。 */
  value: DateRange;
  /** 區間變動時回呼。**留空就是沒有那一端**,不要自己補一個日期上去。 */
  onChange: (next: DateRange) => void;
}

export function DateRangeFilter({ label, value, onChange }: DateRangeFilterProps) {
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState<CSSProperties | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const firstFieldRef = useRef<HTMLInputElement | null>(null);
  const panelId = `nq-date-range-${useId()}`;

  // 設了幾端。一端也算「有在篩」——「九月一日之後的所有單」是完全正常的問法,
  // 不能因為只填了一格就當作沒有條件。
  const count = (value.from === "" ? 0 : 1) + (value.to === "" ? 0 : 1);

  /** 依漏斗的位置算出浮層的固定座標,下方空間不足時往上翻。 */
  const place = useCallback(() => {
    const anchor = triggerRef.current;
    if (
      !anchor ||
      typeof window === "undefined" ||
      typeof anchor.getBoundingClientRect !== "function"
    ) {
      return;
    }
    const rect = anchor.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const flipUp = spaceBelow < PANEL_HEIGHT && rect.top > spaceBelow;
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - PANEL_WIDTH - 8));
    setStyle({
      position: "fixed",
      left,
      width: PANEL_WIDTH,
      // 視窗比浮層還窄時(手機橫放、分割視窗)寧可讓它縮,也不要橫向撐破畫面。
      maxWidth: "calc(100vw - 1rem)",
      ...(flipUp
        ? { bottom: Math.max(8, window.innerHeight - rect.top + 4) }
        : { top: rect.bottom + 4 }),
    });
  }, []);

  /** 關閉並把焦點還給漏斗 —— 鍵盤使用者不能被丟回頁首。 */
  const close = useCallback((restoreFocus = true) => {
    setOpen(false);
    if (restoreFocus) triggerRef.current?.focus();
  }, []);

  const toggleOpen = () => {
    if (open) {
      close();
      return;
    }
    place();
    setOpen(true);
  };

  // 開啟時焦點落在起日那一格:多數人要填的是「從哪天開始」。
  useEffect(() => {
    if (!open) return;
    firstFieldRef.current?.focus();
  }, [open]);

  // 開著的時候跟著捲動與縮放走。用捕獲階段才收得到表格容器自己的捲動。
  useEffect(() => {
    if (!open || typeof window === "undefined") return;
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open, place]);

  // 點到浮層與漏斗以外的地方就收起來。這裡不還焦點:使用者是用滑鼠離開的。
  useEffect(() => {
    if (!open || typeof document === "undefined") return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (triggerRef.current?.contains(target)) return;
      if (document.getElementById(panelId)?.contains(target)) return;
      close(false);
    };
    document.addEventListener("mousedown", onPointerDown, true);
    return () => document.removeEventListener("mousedown", onPointerDown, true);
  }, [open, panelId, close]);

  const onPanelKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape" || event.key === "Tab") {
      // 浮層是浮在頁面上的,Tab 出去只會落在被它蓋住的表格上;而 Escape 綁在面板,
      // 焦點一離開面板就再也關不掉它。兩個鍵都直接收起來,焦點跟著還給漏斗。
      event.preventDefault();
      close();
    }
  };

  const panel =
    open && style !== null && typeof document !== "undefined"
      ? createPortal(
          <div
            id={panelId}
            role="dialog"
            aria-label={`篩選${label}`}
            style={style}
            onKeyDown={onPanelKeyDown}
            className={
              "z-50 flex flex-col gap-2 rounded-[var(--radius)] border border-[var(--border-strong)] " +
              "bg-[var(--surface)] p-2 shadow-card"
            }
          >
            <Field
              ref={firstFieldRef}
              label={`${label}起日`}
              value={value.from}
              onChange={(from) => onChange({ from, to: value.to })}
            />
            <Field
              label={`${label}迄日`}
              value={value.to}
              onChange={(to) => onChange({ from: value.from, to })}
            />

            {/* 含不含當天要寫出來。使用者說的 9/1 到 9/30 是包含 9/30 的,
                系統若其實只到 9/29,他會以為那天的單不見了。 */}
            <p className="m-0 px-0.5 text-xs text-[var(--text-3)]">含起訖當天。留空即不限。</p>

            <div className="flex items-center justify-between gap-2 border-t border-[var(--border)] pt-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onChange({ from: "", to: "" })}
              >
                清除
              </Button>
              <Button type="button" variant="secondary" size="sm" onClick={() => close()}>
                完成
              </Button>
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        // 設了幾端要用**數字**說。只讓漏斗變色的話,色弱與單色列印都讀不出來 ——
        // 一端都沒設時不顯示數字:那時候是「不限」,擺一個 0 反而像是全被篩掉了。
        aria-label={count === 0 ? `篩選${label}` : `篩選${label},已設定 ${count} 項`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={toggleOpen}
        className={
          "inline-flex items-center gap-1 rounded-[var(--radius)] px-1 py-0.5 align-middle " +
          (count === 0
            ? "text-[var(--text-3)] hover:bg-[var(--surface)] hover:text-[var(--text)]"
            : "text-[var(--text)] hover:bg-[var(--surface)]")
        }
      >
        <ListFilter aria-hidden="true" size={14} />
        {count > 0 ? (
          <span className="tabular text-[11px] font-semibold leading-none">{count}</span>
        ) : null}
      </button>
      {panel}
    </>
  );
}

interface FieldProps {
  label: string;
  value: string;
  onChange: (next: string) => void;
}

/** 一格日期。標籤是看得見的文字,不是只有 placeholder ——
    placeholder 一打字就消失,使用者填到一半就分不出這格是起日還是迄日。 */
const Field = forwardRef<HTMLInputElement, FieldProps>(function Field(
  { label, value, onChange },
  ref,
) {
  return (
    <label className="flex flex-col gap-1 text-xs font-medium text-[var(--text-2)]">
      {label}
      <input
        ref={ref}
        type="date"
        value={value}
        aria-label={label}
        onChange={(event) => onChange(event.target.value)}
        className={
          "h-8 w-full min-w-0 rounded-[var(--radius)] border border-[var(--border)] " +
          "bg-[var(--surface)] px-2 text-sm font-normal text-[var(--text)]"
        }
      />
    </label>
  );
});
